import EditNoteIcon from "@mui/icons-material/EditNote";
import HistoryIcon from "@mui/icons-material/History";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import UpdateIcon from "@mui/icons-material/Update";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { Link } from "react-router";
import { FieldValue } from "work/work-agreements/_views/view/tabs/work-agreement-details-field";
import WorkAgreement from "work/work-agreements/work-agreement";

type Props = Readonly<{
  workAgreement: WorkAgreement;
}>;

/**
 * The two ends of a revision, each pointing at the other.
 *
 * Hiring a revision ends the agreement it revised and starts a new one in its place, and the two
 * carry a link in both directions: the ended agreement names the revision that replaced it, and
 * the revision names the agreement it revised. Anyone reading either can get to the other.
 *
 * The routes are the archived and active views respectively, which is where each normally lives
 * -- a replaced agreement has ended, and the agreement that replaced it is the live one. Either
 * page corrects its own route from the agreement's archive state, so a revision that has itself
 * since ended still opens.
 *
 * A third link points the other way in time: while a revision is still being negotiated, the
 * agreement it was opened from names it, so the leaders reading live work can reach the change being
 * asked of it without hunting for it in the proposals list. The server decides who is told -- an
 * unsubmitted draft is private to the party that opened it -- so the id being here at all is the
 * permission to show it.
 *
 * A fourth link points back to where this agreement itself came from: the proposal that was hired
 * to create it, whether this is a first-time agreement or a revision (#5692). That proposal is
 * archived by the time this agreement can be read, so the link opens the archived proposal view.
 *
 * Renders nothing for an agreement that is none of those: a first-time agreement whose hiring
 * proposal cannot be read, with nothing in flight against it either, has nothing to point at.
 */
export default function WorkAgreementRevisionLinks({ workAgreement }: Props) {
  const { openRevisionProposalId, replacedByWorkAgreementId, replacesWorkAgreementId, sourceProposalId } =
    workAgreement;
  if (!openRevisionProposalId && !replacedByWorkAgreementId && !replacesWorkAgreementId && !sourceProposalId)
    return null;

  return (
    <Stack spacing={1.5}>
      {sourceProposalId && (
        <Stack spacing={0.5}>
          <Button
            component={Link}
            size="small"
            startIcon={<RequestQuoteIcon />}
            sx={{ alignSelf: "flex-start" }}
            to={`/proposals/archived/view/${sourceProposalId.value}/details`}
          >
            Open the hired proposal
          </Button>
          <FieldValue>
            This agreement began when this proposal was hired.
          </FieldValue>
        </Stack>
      )}
      {openRevisionProposalId && (
        <Stack spacing={0.5}>
          <Button
            component={Link}
            size="small"
            startIcon={<EditNoteIcon />}
            sx={{ alignSelf: "flex-start" }}
            to={`/proposals/active/revise/${openRevisionProposalId.value}/details`}
          >
            Open the revision request
          </Button>
          <FieldValue>
            A revision of this agreement is being negotiated. This agreement runs unchanged until that
            revision is hired.
          </FieldValue>
        </Stack>
      )}
      {replacedByWorkAgreementId && (
        <Stack spacing={0.5}>
          <Button
            component={Link}
            size="small"
            startIcon={<UpdateIcon />}
            sx={{ alignSelf: "flex-start" }}
            to={`/work/active/view/${replacedByWorkAgreementId.value}/details`}
          >
            Open the current revision of this work
          </Button>
          <FieldValue>
            This agreement ended when a revision of it was hired. The current revision replaced it.
          </FieldValue>
        </Stack>
      )}
      {replacesWorkAgreementId && (
        <Stack spacing={0.5}>
          <Button
            component={Link}
            size="small"
            startIcon={<HistoryIcon />}
            sx={{ alignSelf: "flex-start" }}
            to={`/work/archived/view/${replacesWorkAgreementId.value}/details`}
          >
            Open the previous revision of this work
          </Button>
          <FieldValue>
            This agreement was negotiated as a revision of the previous one, which ended when this was hired.
          </FieldValue>
        </Stack>
      )}
    </Stack>
  );
}
